import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms'; // Import FormsModule for ngModel

// PrimeNG Modules
import { Table, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { RippleModule } from 'primeng/ripple';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { TooltipModule } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { TagModule } from 'primeng/tag';
import { BadgeModule } from 'primeng/badge';
import { OverlayBadgeModule } from 'primeng/overlaybadge';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';

// App Services
import { DataService } from '../../services/data';
import { MemberService } from '../../services/member.service';
import { ReportePdfService } from '../../services/reporte-pdf.service';
import { Subscription, combineLatest, firstValueFrom, take } from 'rxjs';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { AgePipe } from '../../services/edad.pipe';

//Spinner y Messaging
import { SpinnerService } from '../../services/spinner.service';
import { ToastService } from '../../services/toast.service';
import { Member } from '../../models/member.model';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { FamiliesService, FamilyRole } from '../../services/familes.service';
import { Timestamp } from '@angular/fire/firestore';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FocnaAgeKey, getFocnaAgeBucket } from '../../utils/focna-age.util';
import { MissionConfigService } from '../../services/mission-config.service';

type MemberFilterCard =
  | 'todos'
  | 'hombres'
  | 'mujeres'
  | 'activos'
  | 'inactivos'
  | 'bautizados'
  | 'noBautizados'
  | 'visitantes'
  | 'miembros'
  | 'disciplinados'
  | 'familias'
  | FocnaAgeKey;

interface MiembroCard {
  id: string;
  nombres: string;
  apellidos?: string;
  nombreCompleto: string;
  role: 'PADRE' | 'MADRE' | 'HIJO';
  photoUrl?: string | null;
  fechaNacimiento?: Date | Timestamp;
  displayPhotoUrl: string; // con fallback
}

interface FamiliaView {
  familiaId: string;
  nombreFamilia: string;
  padre: MiembroCard | null;
  madre: MiembroCard | null;
  hijos: MiembroCard[];
}

@Component({
  selector: 'app-members',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    RippleModule,
    IconFieldModule,
    InputIconModule,
    TooltipModule,
    ToastModule,
    ConfirmDialogModule,
    TagModule,
    BadgeModule,
    OverlayBadgeModule,
    DialogModule,
    SelectModule,
    AgePipe,
    TranslateModule
  ],
  templateUrl: './members.html',
  styleUrls: ['./members.css']
})
export class Members implements OnInit, OnDestroy {
  @ViewChild('dt') dt!: Table;
  activeCard: MemberFilterCard = 'todos';

  rows: any[] = [];
  familiasRow: any[] = [];
  private subscriptions: Subscription[] = [];
  globalFilterValue: string = '';
  churchId: string = '';
  titleKey: string = 'MEMBER_LIST.FILTER_TODOS';

  // Variables para el conteo de miembros
  memberCount: number = 0;
  hombresCount: number = 0;
  mujeresCount: number = 0;
  familiasCount: number = 0;
  inactivosCount: number = 0;
  activosCount: number = 0;
  bautizadosCount: number = 0;
  noBautizadosCount: number = 0;
  visitantesCount: number = 0;
  disciplinadosCount: number = 0;
  miembrosCount: number = 0;
  focnaCunaCount: number = 0;
  focnaParvulosCount: number = 0;
  focnaPrimariosCount: number = 0;
  focnaPrincipiantesCount: number = 0;
  focnaPreadolescentesCount: number = 0;
  focnaAdolescentesCount: number = 0;
  total: number = 0;

  familias: boolean = false;

  // Reporte Anual
  showReportDialog: boolean = false;
  selectedYearReport: string | null = null;
  yearsReport: any[] = [];
  generatingReport: boolean = false;

  constructor(
    private router: Router,
    private dataService: DataService,
    private memberService: MemberService,
    private reportePdfService: ReportePdfService,
    private SpinnerService: SpinnerService,
    private toastService: ToastService,
    private confirmationService: ConfirmationService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private missionConfig: MissionConfigService,
  ) {
    // Inicializar años para el reporte (desde 2024 hacia atrás)
    const currentYear = new Date().getFullYear();
    for (let i = currentYear; i >= 2020; i--) {
      this.yearsReport.push({ label: i.toString(), value: i.toString() });
    }
  }

  ngOnInit(): void {
    // Check Permissions
    this.subscriptions.push(
      this.dataService.userRoles$.subscribe(roles => {
        const allowedRoles = ['Admin', 'Pastor', 'Secretaría', 'access_membresia'];
        const hasAccess = roles.some(role => allowedRoles.includes(role));
        if (!hasAccess) {
          this.router.navigate(['/dashboard/main']);
        }
      })
    );

    const dataSub = combineLatest([
      this.dataService.churchId$,
      this.dataService.families$,
      this.dataService.members$,
      this.dataService.membershipByFamily$
    ]).subscribe(([churchId, families, members, byFamily]) => {
      if (churchId) {
        this.rows = members
          .filter(m => {
            if (m.tipo === 'Retirado') {
              return false;
            }
            // Verifica que roles exista y sea array
            if (!m.roles || !Array.isArray(m.roles)) {
              return true; // Si no tiene roles, lo incluimos
            }
            return !m.roles.includes('Pastor');
          })
          .map(m => ({
            ...m,
            focnaRango: getFocnaAgeBucket(m.fechaNacimiento),
          }));
        this.churchId = churchId;
        this.familiasRow = this.composeFamiliaView(families, this.rows, byFamily);
        this.sumarMiembros();
      }
      else {
        this.SpinnerService.hide();
        this.rows = [];
      }
    });

    this.subscriptions.push(dataSub);

    this.subscriptions.push(
      this.translate.onLangChange.subscribe(() => this.cdr.detectChanges())
    );
  }

  sumarMiembros(): void {
    this.memberCount = this.rows.length;
    this.total = this.memberCount;
    this.hombresCount = this.rows.filter(member => member.genero === 'M').length;
    this.mujeresCount = this.rows.filter(member => member.genero === 'F').length;

    this.familiasCount = this.familiasRow.filter(fam => !!fam.familiaId).length;

    this.inactivosCount = this.rows.filter(member => member.estadoParticipacion === 'Inactivo').length;
    this.activosCount = this.rows.filter(member => member.estadoParticipacion === 'Activo').length;
    this.bautizadosCount = this.rows.filter(member => member.bautismo === 'Bautizado').length;
    this.noBautizadosCount = this.rows.filter(member => member.bautismo === 'No Bautizado').length;
    this.visitantesCount = this.rows.filter(member => member.tipo === 'Visitante').length;
    this.miembrosCount = this.rows.filter(member => member.tipo === 'Miembro').length;
    this.disciplinadosCount = this.rows.filter(member => member.estadoParticipacion === 'En Disciplina').length;
    this.focnaCunaCount = this.rows.filter(member => member.focnaRango === 'cuna').length;
    this.focnaParvulosCount = this.rows.filter(member => member.focnaRango === 'parvulos').length;
    this.focnaPrimariosCount = this.rows.filter(member => member.focnaRango === 'primarios').length;
    this.focnaPrincipiantesCount = this.rows.filter(member => member.focnaRango === 'principiantes').length;
    this.focnaPreadolescentesCount = this.rows.filter(member => member.focnaRango === 'preadolescentes').length;
    this.focnaAdolescentesCount = this.rows.filter(member => member.focnaRango === 'adolescentes').length;
  }

  applyGlobalFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.globalFilterValue = filterValue; // Actualiza la propiedad del componente
    this.dt.filterGlobal(filterValue, 'contains');
  }

  exportMembersToCustomCSV(): void {
    if (!this.rows || this.rows.length === 0) {
      this.toastService.showInfo(this.translate.instant('MEMBER_LIST.TOAST_NO_EXPORT_DATA'));
      return;
    }

    const loc = this.translate.getCurrentLang() === 'it' ? 'it-IT' : 'es-ES';
    const na = this.translate.instant('MEMBER_LIST.CSV_NA');
    const csvLines: string[] = [];

    csvLines.push(this.translate.instant('MEMBER_LIST.CSV_TITLE'));
    const fechaExportacion = new Date().toLocaleDateString(loc, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    csvLines.push(this.translate.instant('MEMBER_LIST.CSV_GENERATED', { date: fechaExportacion }));
    csvLines.push('');

    csvLines.push(this.translate.instant('MEMBER_LIST.CSV_STATS_HEADER'));
    csvLines.push('');

    csvLines.push(`${this.translate.instant('MEMBER_LIST.CSV_CATEGORY')},${this.translate.instant('MEMBER_LIST.CSV_QUANTITY')}`);
    csvLines.push(`${this.translate.instant('MEMBER_LIST.CSV_TOTAL_REGISTERED')},${this.memberCount}`);
    csvLines.push(`${this.translate.instant('MEMBER_LIST.PDF_STAT_ACTIVE')},${this.activosCount}`);
    csvLines.push(`${this.translate.instant('MEMBER_LIST.PDF_STAT_INACTIVE')},${this.inactivosCount}`);
    csvLines.push(`${this.translate.instant('MEMBER_LIST.PDF_STAT_DISCIPLINE')},${this.disciplinadosCount}`);
    csvLines.push('');

    csvLines.push(`${this.translate.instant('MEMBER_LIST.PDF_STAT_MEMBERS')},${this.miembrosCount}`);
    csvLines.push(`${this.translate.instant('MEMBER_LIST.PDF_STAT_VISITORS')},${this.visitantesCount}`);
    csvLines.push(`${this.translate.instant('MEMBER_LIST.PDF_STAT_BAPTIZED')},${this.bautizadosCount}`);
    csvLines.push(`${this.translate.instant('MEMBER_LIST.PDF_STAT_NOT_BAPTIZED')},${this.noBautizadosCount}`);
    csvLines.push('');

    csvLines.push(`${this.translate.instant('MEMBER_LIST.PDF_STAT_MEN')},${this.hombresCount}`);
    csvLines.push(`${this.translate.instant('MEMBER_LIST.PDF_STAT_WOMEN')},${this.mujeresCount}`);
    csvLines.push(`${this.translate.instant('MEMBER_LIST.PDF_STAT_FAMILIES')},${this.familiasCount}`);
    csvLines.push('');
    csvLines.push('');

    csvLines.push(this.translate.instant('MEMBER_LIST.CSV_DETAIL_TITLE'));
    csvLines.push('');

    const headers = [
      this.translate.instant('MEMBER_LIST.CSV_COL_FULL_NAME'),
      this.translate.instant('MEMBER_LIST.CSV_COL_TYPE'),
      this.translate.instant('MEMBER_LIST.CSV_COL_STATUS'),
      this.translate.instant('MEMBER_LIST.CSV_COL_BAPTISM'),
      this.translate.instant('MEMBER_LIST.CSV_COL_GENDER'),
      this.translate.instant('MEMBER_LIST.CSV_COL_AGE')
    ];
    csvLines.push(headers.join(','));

    const dataRows = this.rows.map(member => {
      const nombreCompleto = member.nombreCompleto || `${member.nombres || ''} ${member.apellidos || ''}`.trim();
      const tipo = this.displayTipo(member.tipo) || na;
      const estado = this.displayEstadoParticipacion(member.estadoParticipacion) || na;
      const bautismo = this.displayBautismo(member) || na;
      const genero = member.genero === 'M'
        ? this.translate.instant('MEMBER_LIST.GENDER_MALE')
        : member.genero === 'F'
          ? this.translate.instant('MEMBER_LIST.GENDER_FEMALE')
          : na;

      let edad = na;
      if (member.fechaNacimiento) {
        const edadCalculada = this.calcularTiempo(member.fechaNacimiento);
        edad = edadCalculada || na;
      }

      return [
        `"${nombreCompleto}"`,
        `"${tipo}"`,
        `"${estado}"`,
        `"${bautismo}"`,
        `"${genero}"`,
        `"${edad}"`
      ];
    });

    csvLines.push(...dataRows.map(row => row.join(',')));

    csvLines.push('');
    csvLines.push(this.translate.instant('MEMBER_LIST.CSV_TOTAL_RECORDS', { count: this.rows.length }));

    const csvContent = csvLines.join('\n');

    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    const fileName = `miembros_${new Date().toISOString().split('T')[0]}.csv`;
    a.download = fileName;

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    this.toastService.showSuccess(this.translate.instant('MEMBER_LIST.TOAST_EXPORT_CSV_OK'));
  }

  async exportMembersToPDF(): Promise<void> {
    if (!this.rows || this.rows.length === 0) {
      this.toastService.showInfo(this.translate.instant('MEMBER_LIST.TOAST_NO_EXPORT_DATA'));
      return;
    }

    this.SpinnerService.show();
    try {
      const loc = this.translate.getCurrentLang() === 'it' ? 'it-IT' : 'es-ES';
      const na = this.translate.instant('MEMBER_LIST.CSV_NA');

      const iglesia: any = this.dataService.dataChurch$
        ? await firstValueFrom(this.dataService.dataChurch$.pipe(take(1)))
        : null;
      const churchName =
        iglesia?.churchName || iglesia?.nombre || this.translate.instant('MEMBER_LIST.PDF_CHURCH_FALLBACK');
      const orgName = this.translate.instant('MEMBER_LIST.PDF_ORG_NAME');
      const countryLabel = this.resolvePdfCountryLabel(iglesia);
      const logoDataUrl = await this.loadPdfLogoDataUrl();

      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const marginX = 12;
      let yPosition = 12;

      const fechaExportacion = new Date().toLocaleDateString(loc, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });

      // —— Encabezado (centrado) ——
      const headerTop = yPosition;
      const logoSize = 18;
      if (logoDataUrl) {
        try {
          doc.addImage(logoDataUrl, 'PNG', marginX, headerTop, logoSize, logoSize);
        } catch {
          // Continuar sin logo si el formato falla
        }
      }

      if (countryLabel) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        doc.setTextColor(100, 116, 139);
        doc.text(countryLabel, pageWidth - marginX, headerTop + 6, { align: 'right' });
      }

      const centerX = pageWidth / 2;
      let textY = headerTop + 7;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(15, 41, 66);
      doc.text(orgName.toUpperCase(), centerX, textY, { align: 'center' });
      textY += 7;

      doc.setFontSize(12);
      doc.setTextColor(0, 109, 138);
      const churchLines = doc.splitTextToSize(String(churchName).toUpperCase(), pageWidth - marginX * 2 - 40);
      doc.text(churchLines, centerX, textY, { align: 'center' });
      textY += churchLines.length * 5.5 + 4;

      yPosition = Math.max(headerTop + logoSize + 4, textY);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.setTextColor(44, 62, 80);
      doc.text(this.translate.instant('MEMBER_LIST.PDF_DOC_TITLE'), centerX, yPosition, { align: 'center' });
      yPosition += 5.5;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(100, 116, 139);
      doc.text(
        this.translate.instant('MEMBER_LIST.PDF_GENERATED', { date: fechaExportacion }),
        centerX,
        yPosition,
        { align: 'center' }
      );
      yPosition += 4;

      doc.setDrawColor(0, 137, 172);
      doc.setLineWidth(0.6);
      doc.line(marginX, yPosition, pageWidth - marginX, yPosition);
      yPosition += 6;

      // —— Resumen estadístico ——
      type PdfStat = { label: string; value: number; color: [number, number, number] };

      const drawStatCard = (
        stat: PdfStat,
        cardX: number,
        cardY: number,
        cardWidth: number,
        cardHeight: number
      ) => {
        doc.setFillColor(255, 255, 255);
        doc.setDrawColor(226, 232, 240);
        doc.setLineWidth(0.2);
        doc.roundedRect(cardX, cardY, cardWidth, cardHeight, 1.5, 1.5, 'FD');

        doc.setFillColor(stat.color[0], stat.color[1], stat.color[2]);
        doc.roundedRect(cardX, cardY, 1.8, cardHeight, 0.6, 0.6, 'F');

        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(stat.color[0], stat.color[1], stat.color[2]);
        doc.text(String(stat.value), cardX + cardWidth / 2 + 0.5, cardY + 6.2, { align: 'center' });

        doc.setFontSize(6.2);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 116, 139);
        const labelLines = doc.splitTextToSize(stat.label, cardWidth - 4);
        doc.text(labelLines, cardX + cardWidth / 2 + 0.5, cardY + 10.2, { align: 'center' });
      };

      const drawStatRow = (
        stats: PdfStat[],
        startY: number,
        cardHeight: number
      ): number => {
        const gap = 3;
        const usable = pageWidth - marginX * 2;
        const cardWidth = (usable - gap * (stats.length - 1)) / stats.length;
        stats.forEach((stat, index) => {
          const cardX = marginX + index * (cardWidth + gap);
          drawStatCard(stat, cardX, startY, cardWidth, cardHeight);
        });
        return startY + cardHeight;
      };

      const contentWidth = pageWidth - marginX * 2;
      const statsStartY = yPosition;
      const cardH = 15;
      const sectionTitleH = 7;
      const sectionGap = 3;
      const blockPad = 5;
      // general title + 3 rows + focna title + 1 row
      const statsInnerHeight =
        blockPad +
        sectionTitleH +
        cardH * 3 +
        sectionGap * 2 +
        sectionTitleH +
        sectionGap +
        cardH +
        blockPad;

      doc.setFillColor(248, 250, 252);
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.3);
      doc.roundedRect(marginX, statsStartY, contentWidth, statsInnerHeight, 2.5, 2.5, 'FD');

      yPosition = statsStartY + blockPad + 4;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 41, 59);
      doc.text(this.translate.instant('MEMBER_LIST.PDF_STATS_TITLE'), pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 4;

      const stats1: PdfStat[] = [
        { label: this.translate.instant('MEMBER_LIST.PDF_STAT_TOTAL'), value: this.memberCount, color: [0, 137, 172] },
        { label: this.translate.instant('MEMBER_LIST.PDF_STAT_ACTIVE'), value: this.activosCount, color: [5, 150, 105] },
        { label: this.translate.instant('MEMBER_LIST.PDF_STAT_INACTIVE'), value: this.inactivosCount, color: [225, 29, 72] },
        { label: this.translate.instant('MEMBER_LIST.PDF_STAT_DISCIPLINE'), value: this.disciplinadosCount, color: [217, 119, 6] }
      ];
      yPosition = drawStatRow(stats1, yPosition, cardH) + sectionGap;

      const stats2: PdfStat[] = [
        { label: this.translate.instant('MEMBER_LIST.PDF_STAT_MEMBERS'), value: this.miembrosCount, color: [79, 70, 229] },
        { label: this.translate.instant('MEMBER_LIST.PDF_STAT_VISITORS'), value: this.visitantesCount, color: [234, 88, 12] },
        { label: this.translate.instant('MEMBER_LIST.PDF_STAT_BAPTIZED'), value: this.bautizadosCount, color: [2, 132, 199] },
        { label: this.translate.instant('MEMBER_LIST.PDF_STAT_NOT_BAPTIZED'), value: this.noBautizadosCount, color: [100, 116, 139] }
      ];
      yPosition = drawStatRow(stats2, yPosition, cardH) + sectionGap;

      const stats3: PdfStat[] = [
        { label: this.translate.instant('MEMBER_LIST.PDF_STAT_MEN'), value: this.hombresCount, color: [37, 99, 235] },
        { label: this.translate.instant('MEMBER_LIST.PDF_STAT_WOMEN'), value: this.mujeresCount, color: [219, 39, 119] },
        { label: this.translate.instant('MEMBER_LIST.PDF_STAT_FAMILIES'), value: this.familiasCount, color: [13, 148, 136] }
      ];
      yPosition = drawStatRow(stats3, yPosition, cardH) + sectionGap + 2;

      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 41, 59);
      doc.text(this.translate.instant('MEMBER_LIST.FOCNA_SECTION_TITLE'), pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 3;

      const focnaStats: PdfStat[] = [
        { label: this.translate.instant('MEMBER_LIST.CARD_FOCNA_CUNA'), value: this.focnaCunaCount, color: [0, 137, 172] },
        { label: this.translate.instant('MEMBER_LIST.CARD_FOCNA_PARVULOS'), value: this.focnaParvulosCount, color: [5, 150, 105] },
        { label: this.translate.instant('MEMBER_LIST.CARD_FOCNA_PRIMARIOS'), value: this.focnaPrimariosCount, color: [217, 119, 6] },
        { label: this.translate.instant('MEMBER_LIST.CARD_FOCNA_PRINCIPIANTES'), value: this.focnaPrincipiantesCount, color: [2, 132, 199] },
        { label: this.translate.instant('MEMBER_LIST.CARD_FOCNA_PREADOLESCENTES'), value: this.focnaPreadolescentesCount, color: [124, 58, 237] },
        { label: this.translate.instant('MEMBER_LIST.CARD_FOCNA_ADOLESCENTES'), value: this.focnaAdolescentesCount, color: [15, 41, 66] }
      ];
      yPosition = drawStatRow(focnaStats, yPosition, cardH) + 8;

      const head = [[
        this.translate.instant('MEMBER_LIST.PDF_TABLE_FULL_NAME'),
        this.translate.instant('MEMBER_LIST.PDF_TABLE_TYPE'),
        this.translate.instant('MEMBER_LIST.PDF_TABLE_STATUS'),
        this.translate.instant('MEMBER_LIST.PDF_TABLE_BAPTISM')
      ]];

      const body = this.rows.map(member => [
        member.nombreCompleto || `${member.nombres || ''} ${member.apellidos || ''}`.trim(),
        this.displayTipo(member.tipo) || na,
        this.displayEstadoParticipacion(member.estadoParticipacion) || na,
        this.displayBautismo(member) || na
      ]);

      const statusRawByRow = this.rows.map(m => m.estadoParticipacion || '');

      autoTable(doc, {
        startY: yPosition,
        head: head,
        body: body,
        theme: 'striped' as any,
        styles: {
          fontSize: 9,
          cellPadding: 3,
          overflow: 'linebreak' as any,
          lineColor: [226, 232, 240],
          lineWidth: 0.1
        },
        headStyles: {
          fillColor: [15, 41, 66],
          textColor: [255, 255, 255],
          fontStyle: 'bold' as any,
          fontSize: 9,
          halign: 'center' as any
        },
        alternateRowStyles: {
          fillColor: [248, 250, 252]
        },
        columnStyles: {
          0: { cellWidth: 70, fontStyle: 'bold' as any },
          1: { cellWidth: 32, halign: 'center' as any },
          2: { cellWidth: 38, halign: 'center' as any },
          3: { cellWidth: 38, halign: 'center' as any }
        },
        didParseCell: (data: any) => {
          if (data.column.index === 2 && data.section === 'body') {
            const estado = statusRawByRow[data.row.index];
            let color: number[] = [100, 116, 139];
            if (estado === 'Activo') color = [5, 150, 105];
            else if (estado === 'Inactivo') color = [225, 29, 72];
            else if (estado === 'En Disciplina') color = [217, 119, 6];
            data.cell.styles.textColor = color;
            data.cell.styles.fontStyle = 'bold';
          }
        },
        margin: { top: 10, left: marginX, right: marginX, bottom: 16 },
        didDrawPage: () => {
          const pageCount = (doc as any).internal.getNumberOfPages();
          const currentPage = (doc as any).internal.getCurrentPageInfo().pageNumber;
          doc.setFontSize(8);
          doc.setTextColor(148, 163, 184);
          doc.setFont('helvetica', 'normal');
          doc.text(
            this.translate.instant('MEMBER_LIST.PDF_PAGE', { current: currentPage, total: pageCount }),
            pageWidth / 2,
            pageHeight - 8,
            { align: 'center' }
          );
        }
      });

      const safeChurch = String(churchName).replace(/[^\w\-]+/g, '_').slice(0, 40);
      const fileName = `miembros_${safeChurch}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      this.toastService.showSuccess(this.translate.instant('MEMBER_LIST.TOAST_EXPORT_PDF_OK'));
    } catch (err) {
      console.error(err);
      this.toastService.showError(this.translate.instant('MEMBER_LIST.TOAST_PDF_ERR'));
    } finally {
      this.SpinnerService.hide();
    }
  }

  private async loadPdfLogoDataUrl(): Promise<string> {
    try {
      const res = await fetch('assets/images/logo_mmm.png');
      if (!res.ok) return '';
      const blob = await res.blob();
      return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch {
      return '';
    }
  }

  private resolvePdfCountryLabel(iglesia: any): string {
    const fromChurch = String(
      iglesia?.pais || iglesia?.churchCountry || iglesia?.country || ''
    ).trim();
    if (fromChurch) return fromChurch.toUpperCase();

    const missionId = (this.missionConfig.getMissionId() || '').toLowerCase();
    if (missionId === 'italia') return 'ITALIA';
    if (missionId === 'espana' || missionId === 'españa') return 'ESPAÑA';
    if (missionId === 'demo') return 'DEMO';
    return '';
  }

  onAddMember(): void {
    this.router.navigate(['/dashboard/members/add']);
  }

  onEditMember(memberId: string): void {
    this.router.navigate(['/dashboard/members/edit', memberId]);
  }

  confirmarInactivarMiembro(member: Member) {
    if (member.uid) {
      // Es un líder, se necesita confirmación para remover cargos ANTES de retirar.
      this.confirmationService.confirm({
        message: this.translate.instant('MEMBER_LIST.CONFIRM_LEADER_MESSAGE'),
        header: this.translate.instant('MEMBER_LIST.CONFIRM_LEADER_HEADER'),
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: this.translate.instant('MEMBER_LIST.CONFIRM_LEADER_ACCEPT'),
        rejectLabel: this.translate.instant('COMMON.CANCEL'),
        acceptButtonStyleClass: 'p-button-danger',
        rejectButtonStyleClass: 'p-button-secondary',
        accept: () => {
          this.removerLiderazgoYRetirar(member);
        }
      });
    } else {
      // No es un líder, solo se necesita confirmación para retirar.
      this.confirmationService.confirm({
        message: this.translate.instant('MEMBER_LIST.CONFIRM_RETIRE_MESSAGE', { name: member.nombres }),
        header: this.translate.instant('MEMBER_LIST.CONFIRM_RETIRE_HEADER'),
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: this.translate.instant('MEMBER_LIST.CONFIRM_RETIRE_ACCEPT'),
        rejectLabel: this.translate.instant('COMMON.CANCEL'),
        acceptButtonStyleClass: 'p-button-danger',
        rejectButtonStyleClass: 'p-button-secondary',
        accept: () => {
          this.marcarComoRetirado(member);
        }
      });
    }
  }

  private marcarComoRetirado(member: Member) {
    this.SpinnerService.show();
    const functions = getFunctions();
    const marcarRetiradoFn = httpsCallable(functions, 'marcarMiembroComoRetirado');

    marcarRetiradoFn({ churchId: this.churchId, miembroId: member.id })
      .then(() => {
        this.toastService.showSuccess(this.translate.instant('MEMBER_LIST.TOAST_RETIRED_OK'));
      })
      .catch((error) => {
        this.toastService.showError(this.translate.instant('MEMBER_LIST.TOAST_RETIRED_ERR'));
      })
      .finally(() => {
        this.SpinnerService.hide();
      });
  }

  private removerLiderazgoYRetirar(member: Member) {
    this.SpinnerService.show();
    const functions = getFunctions();
    const removeMemberCallable = httpsCallable(functions, 'removerUsuarioDeLiderazgo');

    removeMemberCallable({ churchId: this.churchId, uid: member.uid, idDocMember: member.id })
      .then(() => {
        this.toastService.showInfo(this.translate.instant('MEMBER_LIST.TOAST_LEADER_REMOVED'));
        // Si el liderazgo se removió con éxito, procedemos a retirar al miembro.
        // La función marcarComoRetirado se encargará de ocultar el spinner al finalizar.
        this.marcarComoRetirado(member);
      })
      .catch((error) => {
        this.toastService.showError(this.translate.instant('MEMBER_LIST.TOAST_LEADER_ERR'));
        // Ocultamos el spinner aquí solo si el primer paso falla.
        this.SpinnerService.hide();
      });
  }

  confirmarActivarMiembro(member: Member) {
    if (member.id) {
      this.confirmationService.confirm({
        message: this.translate.instant('MEMBER_LIST.CONFIRM_ACTIVATE_MESSAGE', { name: member.nombres }),
        header: this.translate.instant('MEMBER_LIST.CONFIRM_ACTIVATE_HEADER'),
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: this.translate.instant('MEMBER_LIST.CONFIRM_ACTIVATE_ACCEPT'),
        rejectLabel: this.translate.instant('COMMON.CANCEL'),
        acceptButtonStyleClass: 'p-button-success',
        rejectButtonStyleClass: 'p-button-secondary',
        accept: () => {
          this.activarMiembro(member);
        }
      });
    }
  }

  activarMiembro(member: Member) {
    this.SpinnerService.show();
    const functions = getFunctions();
    const activarMiembroCallable = httpsCallable(functions, 'activarMiembroRetirado');
    activarMiembroCallable({ churchId: this.churchId, miembroId: member.id })
      .then(() => {
        this.toastService.showSuccess(this.translate.instant('MEMBER_LIST.TOAST_ACTIVATED_OK'));
      })
      .catch((error) => {
        this.toastService.showError(this.translate.instant('MEMBER_LIST.TOAST_ACTIVATED_ERR'));
      })
      .finally(() => {
        this.SpinnerService.hide();
      });
  }

  getRolesYMinisterios(member: Member): string {
    const resultados: string[] = [];

    // Roles simples
    if (member.roles && member.roles.length > 0) {
      if (typeof member.roles[0] === 'string') {
        resultados.push(
          ...member.roles
            .filter((r: string) => r.toLowerCase() !== 'liderazgo')  // omite "liderazgo"
            .map((r: string) => this.capitalize(r))
        );
      } else {
        resultados.push(
          ...member.roles
            .filter((r: any) => r.value?.toLowerCase() !== 'liderazgo')  // omite si value es "liderazgo"
            .map((r: any) => this.capitalize(r.label))
        );
      }
    }
    // Ministerios: id/rol
    if (member.ministerios && member.ministerios.length > 0) {
      const ministerios = member.ministerios.map((m: any) => {
        const id = this.capitalize(m.id || '');
        const rol = this.capitalize(m.rol || '');
        return `${id} / ${rol}`;
      });
      resultados.push(...ministerios);
    }

    return resultados.join(', ');
  }

  capitalize(text: string): string {
    if (!text) return '';
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  getStatus(status: string): "success" | "info" | "danger" {
    switch (status) {
      case 'Activo':
        return 'success';
      case 'Inactivo':
        return 'danger';
      default:
        return 'info';
    }
  }

  /** Valores almacenados en español → etiqueta según idioma (alineado con selects de ficha miembro). */
  displayTipo(raw: string | null | undefined): string {
    const v = String(raw ?? '').trim();
    if (!v) return '';
    const map: Record<string, string> = {
      Miembro: 'MEMBER_DETAIL.OPT_TYPE_MEMBER',
      Visitante: 'MEMBER_DETAIL.OPT_TYPE_VISITOR',
      'Nuevo Creyente': 'MEMBER_DETAIL.OPT_TYPE_NEW_BELIEVER',
      Retirado: 'MEMBER_DETAIL.OPT_TYPE_RETIRED',
    };
    const key = map[v];
    return key ? this.translate.instant(key) : v;
  }

  displayEstadoParticipacion(raw: string | null | undefined): string {
    const v = String(raw ?? '').trim();
    if (!v) return '';
    const map: Record<string, string> = {
      Activo: 'MEMBER_DETAIL.OPT_STATUS_ACTIVE',
      Inactivo: 'MEMBER_DETAIL.OPT_STATUS_INACTIVE',
      'No Reconocido': 'MEMBER_DETAIL.OPT_STATUS_UNRECOGNIZED',
      'En Disciplina': 'MEMBER_DETAIL.OPT_STATUS_DISCIPLINE',
    };
    const key = map[v];
    return key ? this.translate.instant(key) : v;
  }

  displayBautismo(member: { bautismo?: string | null; estadoEspiritual?: string | null }): string {
    const raw = member?.bautismo ?? member?.estadoEspiritual ?? '';
    const v = String(raw ?? '').trim();
    if (!v) return '';
    const map: Record<string, string> = {
      Bautizado: 'MEMBER_DETAIL.OPT_BAP_BAPTIZED',
      'No Bautizado': 'MEMBER_DETAIL.OPT_BAP_NOT_BAPTIZED',
      'En Proceso': 'MEMBER_DETAIL.OPT_BAP_IN_PROGRESS',
    };
    const key = map[v];
    return key ? this.translate.instant(key) : v;
  }

  onCardFilter(tipo: typeof this.activeCard) {
    this.activeCard = tipo;
    this.dt.clear();

    switch (tipo) {
      case 'hombres':
        this.familias = false;
        this.total = this.hombresCount;
        this.titleKey = 'MEMBER_LIST.FILTER_HOMBRES';
        this.dt.filter('M', 'genero', 'equals');
        break;
      case 'mujeres':
        this.familias = false;
        this.total = this.mujeresCount;
        this.titleKey = 'MEMBER_LIST.FILTER_MUJERES';
        this.dt.filter('F', 'genero', 'equals');
        break;
      case 'activos':
        this.familias = false;
        this.total = this.activosCount;
        this.titleKey = 'MEMBER_LIST.FILTER_ACTIVOS';
        this.dt.filter('Activo', 'estadoParticipacion', 'equals');
        break;
      case 'inactivos':
        this.familias = false;
        this.total = this.inactivosCount;
        this.titleKey = 'MEMBER_LIST.FILTER_INACTIVOS';
        this.dt.filter('Inactivo', 'estadoParticipacion', 'equals');
        break;
      case 'bautizados':
        this.familias = false;
        this.total = this.bautizadosCount;
        this.titleKey = 'MEMBER_LIST.FILTER_BAUTIZADOS';
        this.dt.filter('Bautizado', 'bautismo', 'equals');
        break;
      case 'noBautizados':
        this.familias = false;
        this.total = this.noBautizadosCount;
        this.titleKey = 'MEMBER_LIST.FILTER_NO_BAUTIZADOS';
        this.dt.filter('No Bautizado', 'bautismo', 'equals');
        break;
      case 'visitantes':
        this.familias = false;
        this.total = this.visitantesCount;
        this.titleKey = 'MEMBER_LIST.FILTER_VISITANTES';
        this.dt.filter('Visitante', 'tipo', 'equals');
        break;
      case 'familias':
        this.familias = true;
        this.total = this.familiasCount;
        this.titleKey = 'MEMBER_LIST.FILTER_FAMILIAS';
        break;
      case 'miembros':
        this.familias = false;
        this.total = this.miembrosCount;
        this.titleKey = 'MEMBER_LIST.FILTER_MIEMBROS';
        this.dt.filter('Miembro', 'tipo', 'equals');
        break;
      case 'disciplinados':
        this.familias = false;
        this.total = this.disciplinadosCount;
        this.titleKey = 'MEMBER_LIST.FILTER_DISCIPLINADOS';
        this.dt.filter('En Disciplina', 'estadoParticipacion', 'equals');
        break;
      case 'cuna':
        this.familias = false;
        this.total = this.focnaCunaCount;
        this.titleKey = 'MEMBER_LIST.FILTER_FOCNA_CUNA';
        this.dt.filter('cuna', 'focnaRango', 'equals');
        break;
      case 'parvulos':
        this.familias = false;
        this.total = this.focnaParvulosCount;
        this.titleKey = 'MEMBER_LIST.FILTER_FOCNA_PARVULOS';
        this.dt.filter('parvulos', 'focnaRango', 'equals');
        break;
      case 'primarios':
        this.familias = false;
        this.total = this.focnaPrimariosCount;
        this.titleKey = 'MEMBER_LIST.FILTER_FOCNA_PRIMARIOS';
        this.dt.filter('primarios', 'focnaRango', 'equals');
        break;
      case 'principiantes':
        this.familias = false;
        this.total = this.focnaPrincipiantesCount;
        this.titleKey = 'MEMBER_LIST.FILTER_FOCNA_PRINCIPIANTES';
        this.dt.filter('principiantes', 'focnaRango', 'equals');
        break;
      case 'preadolescentes':
        this.familias = false;
        this.total = this.focnaPreadolescentesCount;
        this.titleKey = 'MEMBER_LIST.FILTER_FOCNA_PREADOLESCENTES';
        this.dt.filter('preadolescentes', 'focnaRango', 'equals');
        break;
      case 'adolescentes':
        this.familias = false;
        this.total = this.focnaAdolescentesCount;
        this.titleKey = 'MEMBER_LIST.FILTER_FOCNA_ADOLESCENTES';
        this.dt.filter('adolescentes', 'focnaRango', 'equals');
        break;
      case 'todos':
      default:
        this.familias = false;
        this.total = this.memberCount;
        this.titleKey = 'MEMBER_LIST.FILTER_TODOS';
        break;
    }
  }

  private composeFamiliaView(
    families: any[],
    members: any[],
    byFamily: Record<string, { familyId: string; memberId: string; role: 'PADRE' | 'MADRE' | 'HIJO' }[]>
  ): FamiliaView[] {

    // Safety checks
    if (!families || !Array.isArray(families) || families.length === 0) {
      return [];
    }

    if (!members || !Array.isArray(members)) {
      return [];
    }

    if (!byFamily || typeof byFamily !== 'object') {
      return [];
    }

    const membersMap = new Map<string, any>(
      members.filter(m => !!m?.id).map(m => [m.id, m])
    );

    const toMiembroCard = (mFull: any, role: MiembroCard['role']): MiembroCard => {
      const nombreCompleto = `${mFull?.nombres ?? ''} ${mFull?.apellidos ?? ''}`.trim();
      const photoUrl = mFull?.photoUrl || mFull?.fotoUrl || null;
      return {
        id: mFull.id,
        nombres: mFull?.nombres ?? '',
        apellidos: mFull?.apellidos ?? '',
        nombreCompleto,
        role,
        photoUrl,
        fechaNacimiento: mFull?.fechaNacimiento,
        displayPhotoUrl: photoUrl || 'assets/avatar.png'
      };
    };

    const result: FamiliaView[] = [];

    for (const fam of families) {
      const fid = fam.familyId || fam.id;
      if (!fid) continue;

      const ms = byFamily[fid] || [];
      let padre: MiembroCard | null = null;
      let madre: MiembroCard | null = null;
      const hijos: MiembroCard[] = [];

      for (const m of ms) {
        const full = membersMap.get(m.memberId);
        if (!full) continue; // aún no cargó el miembro
        const item = toMiembroCard(full, m.role);

        if (m.role === 'PADRE' && !padre) padre = item;
        else if (m.role === 'MADRE' && !madre) madre = item;
        else if (m.role === 'HIJO') hijos.push(item);
      }

      result.push({
        familiaId: fid,
        nombreFamilia: fam.nombreFamilia || fam.nombre || 'Familia',
        padre,
        madre,
        hijos: hijos.sort((a, b) => (a.nombres ?? '').localeCompare(b.nombres ?? ''))
      });
    }

    return result.sort((a, b) => (a.nombreFamilia ?? '').localeCompare(b.nombreFamilia ?? ''));
  }

  onImgError(evt: Event) {
    const el = evt.target as HTMLImageElement;
    el.src = 'assets/avatar.png'; // Fallback image
  }

  scrollChildren(event: Event, direction: 'left' | 'right') {
    event.preventDefault();
    const button = event.currentTarget as HTMLButtonElement;
    const wrapper = button.closest('.children-slider-wrapper');
    const slider = wrapper?.querySelector('.children-slider') as HTMLElement;

    if (slider) {
      const scrollAmount = 200; // Cantidad de px a desplazar
      const currentScroll = slider.scrollLeft;
      const targetScroll = direction === 'left'
        ? currentScroll - scrollAmount
        : currentScroll + scrollAmount;

      slider.scrollTo({
        left: targetScroll,
        behavior: 'smooth'
      });
    }
  }

  calcularTiempo(fechaNacimiento?: Date | Timestamp): string {
    if (!fechaNacimiento) return '';
    let nacimiento: Date;

    if (fechaNacimiento instanceof Timestamp) {
      nacimiento = fechaNacimiento.toDate();
    } else if (fechaNacimiento instanceof Date) {
      nacimiento = fechaNacimiento;
    } else {
      return '';
    }

    const hoy = new Date();

    let edadAnios = hoy.getFullYear() - nacimiento.getFullYear();
    const mes = hoy.getMonth() - nacimiento.getMonth();
    const dia = hoy.getDate() - nacimiento.getDate();

    // Ajuste si aún no ha cumplido años este año
    if (mes < 0 || (mes === 0 && dia < 0)) {
      edadAnios--;
    }

    if (edadAnios >= 1) {
      return edadAnios === 1
        ? this.translate.instant('MEMBER_LIST.AGE_ONE_YEAR')
        : this.translate.instant('MEMBER_LIST.AGE_YEARS_N', { n: edadAnios });
    }

    let edadMeses = hoy.getMonth() - nacimiento.getMonth();
    if (edadMeses < 0) edadMeses += 12;
    if (hoy.getDate() < nacimiento.getDate()) edadMeses--;

    edadMeses = edadMeses < 0 ? 0 : edadMeses;

    return edadMeses === 1
      ? this.translate.instant('MEMBER_LIST.AGE_ONE_MONTH')
      : this.translate.instant('MEMBER_LIST.AGE_MONTHS_N', { n: edadMeses });
  }

  // REPORTE ANUAL
  openReportDialog() {
    this.showReportDialog = true;
    this.selectedYearReport = (new Date().getFullYear() - 1).toString();
  }

  async generateAnnualReport() {
    if (!this.selectedYearReport || !this.churchId) return;

    this.generatingReport = true;
    this.SpinnerService.show();

    try {
      // 1. Obtener la información de la iglesia
      const iglesia: any = this.dataService.dataChurch$ ? await firstValueFrom(this.dataService.dataChurch$.pipe(take(1))) : null;

      // 2. Obtener todos los miembros
      let allMembers = this.dataService.getCurrentMembers();
      if (!allMembers || allMembers.length === 0) {
        allMembers = await firstValueFrom(this.memberService.loadAllMembersForChurch(this.churchId));
      }

      const reportData: any[] = [];
      let totalGeneral = 0;

      // 3. Iterar y buscar diezmos
      for (const member of allMembers) {
        if (!member.id) continue;

        try {
          const summaries = await firstValueFrom(this.memberService.getDiezmoSummaries(this.churchId, member.id));
          const yearSummary = summaries.find((s: any) => s.year === this.selectedYearReport);

          if (yearSummary && yearSummary.total > 0) {
            totalGeneral += yearSummary.total;
            reportData.push({
              nombreCompleto: `${member.nombres} ${member.apellidos}`,
              documento: member.numeroDocumento,
              total: yearSummary.total
            });
          }
        } catch (e) {

        }
      }

      if (reportData.length === 0) {
        this.toastService.showInfo(this.translate.instant('MEMBER_LIST.TOAST_NO_TITHES_YEAR', { year: this.selectedYearReport }));
        this.generatingReport = false;
        this.SpinnerService.hide();
        return;
      }

      // Ordenar alfabéticamente
      reportData.sort((a, b) => a.nombreCompleto.localeCompare(b.nombreCompleto));

      // 4. Generar PDF
      const pdfData = {
        anio: this.selectedYearReport,
        iglesiaNombre: iglesia?.churchName || 'IGLESIA CRISTIANA PENTECOSTÉS DE ESPAÑA',
        totalGeneral: totalGeneral,
        miembros: reportData
      };

      await this.reportePdfService.generarListadoDiezmosAnual(pdfData);
      this.toastService.showSuccess(this.translate.instant('MEMBER_LIST.TOAST_REPORT_OK'));
      this.showReportDialog = false;

    } catch (err) {
      this.toastService.showError(this.translate.instant('MEMBER_LIST.TOAST_REPORT_ERR'));
    } finally {
      this.generatingReport = false;
      this.SpinnerService.hide();
    }
  }

  ngAfterViewInit(): void {
    this.sumarMiembros();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
}
